import { useState, useEffect, useCallback } from 'react'
import { getProcessos, createProcesso, updateProcesso, deleteProcesso, marcarProntoPagamento, marcarAprovado, marcarPago, alterarStatusProcesso, getAgendaPagamentos, getDashboardFonteRecurso, exportarProcessosExcel, exportarProcessosPDF, pagarParcela, aprovarParcela, programarParcela, parcelaProntoPagamento, retrocederParcela } from '@/services/api'
import { formatCurrency, formatDate, getStatusColor, getStatusLabel, todayLocalISO } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { FileText, Trash2, Edit, CheckCircle, DollarSign, Loader2, Filter, Wallet, MoreHorizontal, ArrowLeft, ArrowRight, ThumbsUp, RotateCcw, Calendar, CalendarDays, Download, FileSpreadsheet, PieChart, Search, CreditCard } from 'lucide-react'
import { toast } from 'sonner'

import ProcessoFormDialog from '@/components/processos/ProcessoFormDialog'
import FonteRecursoDialog from '@/components/processos/FonteRecursoDialog'
import ParcelasDialog from '@/components/processos/ParcelasDialog'
import PagarDialog from '@/components/processos/PagarDialog'
import ProgramadosTab from '@/components/processos/ProgramadosTab'
import AgendaTab from '@/components/processos/AgendaTab'
import DashboardFonteTab from '@/components/processos/DashboardFonteTab'

export default function ProcessosPage() {
  const [processos, setProcessos] = useState([])
  const [agenda, setAgenda] = useState([])
  const [dashboardFonte, setDashboardFonte] = useState(null)
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [fonteRecursoDialogOpen, setFonteRecursoDialogOpen] = useState(false)
  const [parcelasDialogOpen, setParcelasDialogOpen] = useState(false)
  const [parcelaFonteDialogOpen, setParcelaFonteDialogOpen] = useState(false)
  const [pagarDialogOpen, setPagarDialogOpen] = useState(false)
  const [pagarTarget, setPagarTarget] = useState(null)
  const [dataPagamento, setDataPagamento] = useState('')
  const [selectedProcesso, setSelectedProcesso] = useState(null)
  const [selectedProcessoId, setSelectedProcessoId] = useState(null)
  const [selectedParcelaNumero, setSelectedParcelaNumero] = useState(null)
  const [fonteRecurso, setFonteRecurso] = useState('')
  const [previsaoPagamento, setPrevisaoPagamento] = useState('')
  const [editingProcesso, setEditingProcesso] = useState(null)
  const [filterStatus, setFilterStatus] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const [filtroDataInicio, setFiltroDataInicio] = useState('')
  const [filtroDataFim, setFiltroDataFim] = useState('')

  const [formData, setFormData] = useState({
    fornecedor: '', numero_processo: '', valor: '', descricao: '', parcelado: false, numero_parcelas: 1
  })

  const fetchProcessos = useCallback(async () => {
    try {
      const response = await getProcessos(filterStatus || undefined)
      setProcessos(response.data)
    } catch (error) {
      toast.error('Erro ao carregar processos')
    } finally {
      setLoading(false)
    }
  }, [filterStatus])

  const fetchAgenda = useCallback(async () => {
    try {
      const response = await getAgendaPagamentos(filtroDataInicio || undefined, filtroDataFim || undefined)
      setAgenda(response.data)
    } catch (error) {
      toast.error('Erro ao carregar agenda de pagamentos')
    }
  }, [filtroDataInicio, filtroDataFim])

  const fetchDashboardFonte = useCallback(async () => {
    try {
      const response = await getDashboardFonteRecurso()
      setDashboardFonte(response.data)
    } catch (error) {
      toast.error('Erro ao carregar dashboard de fontes')
    }
  }, [])

  useEffect(() => {
    fetchProcessos()
    fetchAgenda()
    fetchDashboardFonte()
  }, [fetchProcessos, fetchAgenda, fetchDashboardFonte])

  const refreshAll = () => {
    fetchProcessos()
    fetchAgenda()
    fetchDashboardFonte()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const data = {
        ...formData,
        valor: parseFloat(formData.valor),
        parcelado: formData.parcelado,
        numero_parcelas: formData.parcelado ? parseInt(formData.numero_parcelas) : 1
      }

      if (editingProcesso) {
        await updateProcesso(editingProcesso.id, data)
        toast.success('Processo atualizado com sucesso!')
      } else {
        await createProcesso(data)
        toast.success('Processo criado com sucesso!')
      }

      setDialogOpen(false)
      setEditingProcesso(null)
      setFormData({ fornecedor: '', numero_processo: '', valor: '', descricao: '', parcelado: false, numero_parcelas: 1 })
      fetchProcessos()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar processo')
    }
  }

  const handleEdit = (processo) => {
    setEditingProcesso(processo)
    setFormData({
      fornecedor: processo.fornecedor,
      numero_processo: processo.numero_processo,
      valor: processo.valor.toString(),
      descricao: processo.descricao,
      parcelado: processo.parcelado || false,
      numero_parcelas: processo.numero_parcelas || 1
    })
    setDialogOpen(true)
  }

  const handleOpenParcelas = (processo) => {
    setSelectedProcesso(processo)
    setParcelasDialogOpen(true)
  }

  const handlePagarParcela = (parcelaNumero) => {
    if (!selectedProcesso) return
    const parcela = selectedProcesso.parcelas?.find(p => p.numero === parcelaNumero)
    setPagarTarget({
      tipo: 'parcela',
      processo_id: selectedProcesso.id,
      parcela_numero: parcelaNumero,
      numero_parcelas: selectedProcesso.numero_parcelas,
      numero_processo: selectedProcesso.numero_processo,
      valor: parcela?.valor,
    })
    setDataPagamento(todayLocalISO())
    setPagarDialogOpen(true)
  }

  const handleOpenPagarProcesso = (processo) => {
    setPagarTarget({
      tipo: 'processo',
      processo_id: processo.id,
      numero_processo: processo.numero_processo,
      valor: processo.valor,
    })
    setDataPagamento(todayLocalISO())
    setPagarDialogOpen(true)
  }

  const handleOpenPagarFromAgenda = (item) => {
    setPagarTarget({
      tipo: item.tipo,
      processo_id: item.processo_id,
      parcela_numero: item.parcela_numero,
      numero_parcelas: item.numero_parcelas,
      numero_processo: item.numero_processo,
      valor: item.valor,
    })
    setDataPagamento(todayLocalISO())
    setPagarDialogOpen(true)
  }

  const handleConfirmarPagamento = async (e) => {
    e.preventDefault()
    if (!pagarTarget) return
    if (!dataPagamento) {
      toast.error('Informe a data do pagamento')
      return
    }
    try {
      if (pagarTarget.tipo === 'parcela') {
        const response = await pagarParcela(pagarTarget.processo_id, pagarTarget.parcela_numero, dataPagamento)
        if (selectedProcesso?.id === pagarTarget.processo_id) setSelectedProcesso(response.data)
        toast.success(`Parcela ${pagarTarget.parcela_numero} paga em ${formatDate(dataPagamento)}!`)
      } else {
        await marcarPago(pagarTarget.processo_id, dataPagamento)
        toast.success(`Processo pago em ${formatDate(dataPagamento)}!`)
      }
      setPagarDialogOpen(false)
      setPagarTarget(null)
      refreshAll()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar pagamento')
    }
  }

  const handleAprovarParcela = async (parcelaNumero) => {
    if (!selectedProcesso) return
    try {
      const response = await aprovarParcela(selectedProcesso.id, parcelaNumero)
      setSelectedProcesso(response.data)
      toast.success(`Parcela ${parcelaNumero} aprovada!`)
      refreshAll()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar parcela')
    }
  }

  const handleProgramarParcela = async (parcelaNumero) => {
    if (!selectedProcesso) return
    try {
      const response = await programarParcela(selectedProcesso.id, parcelaNumero)
      setSelectedProcesso(response.data)
      toast.success(`Parcela ${parcelaNumero} programada!`)
      refreshAll()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao programar parcela')
    }
  }

  const openParcelaFonteDialog = (parcelaNumero) => {
    setSelectedParcelaNumero(parcelaNumero)
    setFonteRecurso('')
    setPrevisaoPagamento('')
    setParcelaFonteDialogOpen(true)
  }

  const handleParcelaProntoPagamento = async (e) => {
    e.preventDefault()
    if (!selectedProcesso || !selectedParcelaNumero) return
    try {
      const response = await parcelaProntoPagamento(selectedProcesso.id, selectedParcelaNumero, {
        fonte_recurso: fonteRecurso,
        previsao_pagamento: previsaoPagamento
      })
      setSelectedProcesso(response.data)
      setParcelaFonteDialogOpen(false)
      toast.success(`Parcela ${selectedParcelaNumero} pronta para pagamento!`)
      refreshAll()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao marcar parcela')
    }
  }

  const handleRetrocederParcela = async (parcelaNumero, novoStatus) => {
    if (!selectedProcesso) return
    try {
      const response = await retrocederParcela(selectedProcesso.id, parcelaNumero, novoStatus)
      setSelectedProcesso(response.data)
      toast.success(`Parcela ${parcelaNumero} retrocedida!`)
      refreshAll()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao retroceder parcela')
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este processo?')) return
    try {
      await deleteProcesso(id)
      toast.success('Processo excluído com sucesso!')
      refreshAll()
    } catch (error) {
      toast.error('Erro ao excluir processo')
    }
  }

  const openFonteRecursoDialog = (processoId) => {
    setSelectedProcessoId(processoId)
    setFonteRecurso('')
    setPrevisaoPagamento('')
    setFonteRecursoDialogOpen(true)
  }

  const handleProntoPagamento = async (e) => {
    e.preventDefault()
    if (!fonteRecurso.trim()) return toast.error('Informe a fonte de recurso')
    if (!previsaoPagamento) return toast.error('Informe a previsão de pagamento')
    try {
      await marcarProntoPagamento(selectedProcessoId, fonteRecurso, previsaoPagamento)
      toast.success('Processo marcado como pronto para pagamento!')
      setFonteRecursoDialogOpen(false)
      setSelectedProcessoId(null)
      setFonteRecurso('')
      setPrevisaoPagamento('')
      refreshAll()
    } catch (error) {
      toast.error('Erro ao atualizar processo')
    }
  }

  const handleAprovado = async (id) => {
    try {
      await marcarAprovado(id)
      toast.success('Processo aprovado! Adicionado à agenda de pagamentos.')
      refreshAll()
    } catch (error) {
      toast.error('Erro ao atualizar processo')
    }
  }

  const handleProgramarProcesso = async (id) => {
    try {
      await alterarStatusProcesso(id, 'programado')
      toast.success('Processo programado!')
      refreshAll()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao programar processo')
    }
  }

  const handleAlterarStatus = async (id, novoStatus) => {
    try {
      await alterarStatusProcesso(id, novoStatus)
      toast.success(`Status alterado para ${getStatusLabel(novoStatus)}!`)
      refreshAll()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar status')
    }
  }

  const handleExportarExcel = async (params = {}) => {
    try {
      toast.info('Gerando relatório Excel...')
      const response = await exportarProcessosExcel(params)
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `processos_${new Date().toISOString().slice(0, 10)}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Relatório Excel gerado com sucesso!')
    } catch (error) {
      toast.error('Erro ao gerar relatório Excel')
    }
  }

  const handleExportarPDF = async (params = {}) => {
    try {
      toast.info('Gerando relatório PDF...')
      const response = await exportarProcessosPDF(params)
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `processos_${new Date().toISOString().slice(0, 10)}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Relatório PDF gerado com sucesso!')
    } catch (error) {
      toast.error('Erro ao gerar relatório PDF')
    }
  }

  const handleAprovarProgramado = async ({ tipo, processo_id, parcela_numero }) => {
    try {
      if (tipo === 'parcela') {
        await aprovarParcela(processo_id, parcela_numero)
        toast.success(`Parcela ${parcela_numero} aprovada!`)
      } else {
        await marcarAprovado(processo_id)
        toast.success('Processo aprovado!')
      }
      refreshAll()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar')
    }
  }

  const filteredProcessos = processos.filter(p => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return (
      p.numero_processo?.toLowerCase().includes(term) ||
      p.fornecedor?.toLowerCase().includes(term) ||
      p.descricao?.toLowerCase().includes(term) ||
      p.fonte_recurso?.toLowerCase().includes(term)
    )
  })

  const totais = {
    abertos: processos.filter(p => p.status === 'aberto'),
    prontos: processos.filter(p => p.status === 'pronto_pagamento'),
    programados: processos.filter(p => p.status === 'programado'),
    aprovados: processos.filter(p => p.status === 'aprovado'),
    pagos: processos.filter(p => p.status === 'pago')
  }

  const getAcoesDisponiveis = (processo) => {
    const acoes = { avancar: [], retroceder: [] }
    switch (processo.status) {
      case 'aberto':
        acoes.avancar = [{ status: 'pronto_pagamento', label: 'Pronto p/ Pagamento', needsFonte: true }]
        break
      case 'pronto_pagamento':
        acoes.avancar = [
          { status: 'programado', label: 'Programar' },
          { status: 'aprovado', label: 'Aprovar para Pagamento' }
        ]
        acoes.retroceder = [{ status: 'aberto', label: 'Voltar para Aberto' }]
        break
      case 'programado':
        acoes.avancar = [{ status: 'aprovado', label: 'Aprovar para Pagamento' }]
        acoes.retroceder = [
          { status: 'pronto_pagamento', label: 'Voltar para Pronto p/ Pagamento' },
          { status: 'aberto', label: 'Voltar para Aberto' }
        ]
        break
      case 'aprovado':
        acoes.avancar = [{ status: 'pago', label: 'Marcar como Pago' }]
        acoes.retroceder = [
          { status: 'programado', label: 'Voltar para Programado' },
          { status: 'pronto_pagamento', label: 'Voltar para Pronto p/ Pagamento' },
          { status: 'aberto', label: 'Voltar para Aberto' }
        ]
        break
      case 'pago':
        acoes.retroceder = [
          { status: 'aprovado', label: 'Voltar para Aprovado' },
          { status: 'programado', label: 'Voltar para Programado' },
          { status: 'pronto_pagamento', label: 'Voltar para Pronto p/ Pagamento' },
          { status: 'aberto', label: 'Voltar para Aberto' }
        ]
        break
      default:
        break
    }
    return acoes
  }

  const totalProgramados =
    processos.filter(p => !p.parcelado && p.status === 'programado').length +
    processos.reduce((count, p) => count + (p.parcelas?.filter(parc => parc.status === 'programado').length || 0), 0)

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Processos</h1>
          <p className="text-slate-500 mt-1">Gerencie os processos e pagamentos</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="btn-exportar">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Exportar Processos</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExportarExcel({ status: 'aprovado' })}>
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                Aprovados (Excel)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportarPDF({ status: 'aprovado' })}>
                <FileText className="w-4 h-4 mr-2 text-rose-600" />
                Aprovados (PDF)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExportarExcel({})}>
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                Todos (Excel)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportarPDF({})}>
                <FileText className="w-4 h-4 mr-2 text-rose-600" />
                Todos (PDF)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <ProcessoFormDialog
            open={dialogOpen}
            onOpenChange={(v) => { setDialogOpen(v); if (!v) { setEditingProcesso(null); setFormData({ fornecedor: '', numero_processo: '', valor: '', descricao: '', parcelado: false, numero_parcelas: 1 }) } }}
            editingProcesso={editingProcesso}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
          />
        </div>
      </div>

      {/* Modais */}
      <FonteRecursoDialog
        open={fonteRecursoDialogOpen}
        onOpenChange={setFonteRecursoDialogOpen}
        fonteRecurso={fonteRecurso}
        setFonteRecurso={setFonteRecurso}
        previsaoPagamento={previsaoPagamento}
        setPrevisaoPagamento={setPrevisaoPagamento}
        onSubmit={handleProntoPagamento}
      />

      <ParcelasDialog
        open={parcelasDialogOpen}
        onOpenChange={setParcelasDialogOpen}
        processo={selectedProcesso}
        onProntoPagamento={openParcelaFonteDialog}
        onProgramar={handleProgramarParcela}
        onAprovar={handleAprovarParcela}
        onPagar={handlePagarParcela}
        onRetroceder={handleRetrocederParcela}
      />

      <PagarDialog
        open={pagarDialogOpen}
        onOpenChange={setPagarDialogOpen}
        target={pagarTarget}
        dataPagamento={dataPagamento}
        setDataPagamento={setDataPagamento}
        onSubmit={handleConfirmarPagamento}
      />

      <FonteRecursoDialog
        open={parcelaFonteDialogOpen}
        onOpenChange={setParcelaFonteDialogOpen}
        title={`Parcela ${selectedParcelaNumero} - Pronto para Pagamento`}
        description="Informe a fonte de recurso e previsão de pagamento da parcela."
        fonteRecurso={fonteRecurso}
        setFonteRecurso={setFonteRecurso}
        previsaoPagamento={previsaoPagamento}
        setPrevisaoPagamento={setPrevisaoPagamento}
        onSubmit={handleParcelaProntoPagamento}
        testIdPrefix="parcela-"
      />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Em Aberto</p>
                <p className="text-2xl font-semibold text-slate-900">{totais.abertos.length}</p>
                <p className="text-sm text-slate-600">{formatCurrency(totais.abertos.reduce((a, p) => a + p.valor, 0))}</p>
              </div>
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700">Pronto p/ Pagamento</p>
                <p className="text-2xl font-semibold text-amber-800">{totais.prontos.length}</p>
                <p className="text-sm text-amber-600">{formatCurrency(totais.prontos.reduce((a, p) => a + p.valor, 0))}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">Aprovados (Agenda)</p>
                <p className="text-2xl font-semibold text-blue-800">{totais.aprovados.length}</p>
                <p className="text-sm text-blue-600">{formatCurrency(totais.aprovados.reduce((a, p) => a + p.valor, 0))}</p>
              </div>
              <CalendarDays className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-700">Pagos</p>
                <p className="text-2xl font-semibold text-emerald-800">{totais.pagos.length}</p>
                <p className="text-sm text-emerald-600">{formatCurrency(totais.pagos.reduce((a, p) => a + p.valor, 0))}</p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="processos" className="space-y-4">
        <TabsList>
          <TabsTrigger value="processos" data-testid="tab-processos">Processos</TabsTrigger>
          <TabsTrigger value="programados" data-testid="tab-programados">
            <Calendar className="w-4 h-4 mr-2" />
            Programados ({totalProgramados})
          </TabsTrigger>
          <TabsTrigger value="agenda" data-testid="tab-agenda">
            <CalendarDays className="w-4 h-4 mr-2" />
            Agenda ({agenda.length})
          </TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard-fonte">
            <PieChart className="w-4 h-4 mr-2" />
            Por Fonte
          </TabsTrigger>
        </TabsList>

        <TabsContent value="processos">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por número, fornecedor ou descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-processos"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-slate-400" />
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-48" data-testid="filter-status">
                  <SelectValue placeholder="Filtrar por status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="aberto">Abertos</SelectItem>
                  <SelectItem value="pronto_pagamento">Pronto p/ Pagamento</SelectItem>
                  <SelectItem value="programado">Programados</SelectItem>
                  <SelectItem value="aprovado">Aprovados</SelectItem>
                  <SelectItem value="pago">Pagos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                </div>
              ) : filteredProcessos.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <FileText className="w-12 h-12 mb-4 text-slate-300" />
                  <p>{searchTerm ? 'Nenhum processo encontrado para a busca' : 'Nenhum processo encontrado'}</p>
                  {searchTerm && (
                    <Button variant="link" onClick={() => setSearchTerm('')} className="mt-2">
                      Limpar busca
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Número</TableHead>
                      <TableHead>Fornecedor</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Fonte Recurso</TableHead>
                      <TableHead>Previsão Pgto</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProcessos.map((processo) => {
                      const acoes = getAcoesDisponiveis(processo)
                      return (
                        <TableRow key={processo.id} className="hover:bg-slate-50" data-testid={`processo-row-${processo.id}`}>
                          <TableCell className="font-medium">{processo.numero_processo}</TableCell>
                          <TableCell>{processo.fornecedor}</TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span>{formatCurrency(processo.valor)}</span>
                              {processo.parcelado && processo.parcelas && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] mt-1 w-fit cursor-pointer hover:bg-teal-50"
                                  onClick={() => handleOpenParcelas(processo)}
                                >
                                  <CreditCard className="w-3 h-3 mr-1" />
                                  {processo.parcelas.filter(p => p.status === 'pago').length}/{processo.numero_parcelas} parcelas
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${getStatusColor(processo.status)} border`}>
                              {getStatusLabel(processo.status)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {processo.fonte_recurso ? (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                <Wallet className="w-3 h-3 mr-1" />
                                {processo.fonte_recurso}
                              </Badge>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {processo.previsao_pagamento ? (
                              <div className="flex items-center gap-1 text-slate-600">
                                <Calendar className="w-3 h-3" />
                                <span className="text-sm">{formatDate(processo.previsao_pagamento)}</span>
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <TooltipProvider>
                                {processo.parcelado && processo.parcelas ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button size="sm" variant="ghost" className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                                        onClick={() => handleOpenParcelas(processo)} data-testid={`btn-parcelas-${processo.id}`}>
                                        <CreditCard className="w-4 h-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Gerenciar Parcelas</TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <>
                                    {acoes.avancar.length > 0 && (
                                      <>
                                        {acoes.avancar[0].needsFonte ? (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button size="sm" variant="ghost" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" onClick={() => openFonteRecursoDialog(processo.id)} data-testid={`btn-avancar-${processo.id}`}>
                                                <ArrowRight className="w-4 h-4" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>{acoes.avancar[0].label}</TooltipContent>
                                          </Tooltip>
                                        ) : processo.status === 'pronto_pagamento' ? (
                                          <>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Button size="sm" variant="ghost" className="text-purple-600 hover:text-purple-700 hover:bg-purple-50" onClick={() => handleProgramarProcesso(processo.id)} data-testid={`btn-programar-${processo.id}`}>
                                                  <Calendar className="w-4 h-4" />
                                                </Button>
                                              </TooltipTrigger>
                                              <TooltipContent>Programar</TooltipContent>
                                            </Tooltip>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => handleAprovado(processo.id)} data-testid={`btn-aprovar-${processo.id}`}>
                                                  <ThumbsUp className="w-4 h-4" />
                                                </Button>
                                              </TooltipTrigger>
                                              <TooltipContent>Aprovar para Pagamento</TooltipContent>
                                            </Tooltip>
                                          </>
                                        ) : processo.status === 'programado' ? (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => handleAprovado(processo.id)} data-testid={`btn-aprovar-${processo.id}`}>
                                                <ThumbsUp className="w-4 h-4" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>Aprovar para Pagamento</TooltipContent>
                                          </Tooltip>
                                        ) : processo.status === 'aprovado' ? (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button size="sm" variant="ghost" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" onClick={() => handleOpenPagarProcesso(processo)} data-testid={`btn-pago-${processo.id}`}>
                                                <DollarSign className="w-4 h-4" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>Marcar como Pago</TooltipContent>
                                          </Tooltip>
                                        ) : null}
                                      </>
                                    )}
                                  </>
                                )}

                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button size="sm" variant="ghost" data-testid={`btn-menu-${processo.id}`}>
                                      <MoreHorizontal className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleEdit(processo)}>
                                      <Edit className="w-4 h-4 mr-2" />
                                      Editar
                                    </DropdownMenuItem>

                                    {processo.parcelado && processo.parcelas && processo.parcelas.length > 0 && (
                                      <DropdownMenuItem onClick={() => handleOpenParcelas(processo)}>
                                        <CreditCard className="w-4 h-4 mr-2 text-teal-600" />
                                        Ver Parcelas ({processo.parcelas.filter(p => p.status === 'pago').length}/{processo.numero_parcelas})
                                      </DropdownMenuItem>
                                    )}

                                    {acoes.retroceder.length > 0 && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuLabel className="text-xs text-slate-500">
                                          <RotateCcw className="w-3 h-3 inline mr-1" />
                                          Retroceder para
                                        </DropdownMenuLabel>
                                        {acoes.retroceder.map((acao) => (
                                          <DropdownMenuItem
                                            key={acao.status}
                                            onClick={() => handleAlterarStatus(processo.id, acao.status)}
                                            className="text-amber-600"
                                          >
                                            <ArrowLeft className="w-4 h-4 mr-2" />
                                            {acao.label}
                                          </DropdownMenuItem>
                                        ))}
                                      </>
                                    )}

                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleDelete(processo.id)} className="text-rose-600">
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Excluir
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TooltipProvider>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="programados">
          <ProgramadosTab processos={processos} onAprovar={handleAprovarProgramado} />
        </TabsContent>

        <TabsContent value="agenda">
          <AgendaTab
            agenda={agenda}
            filtroDataInicio={filtroDataInicio}
            filtroDataFim={filtroDataFim}
            setFiltroDataInicio={setFiltroDataInicio}
            setFiltroDataFim={setFiltroDataFim}
            onFiltrar={fetchAgenda}
            onLimparFiltros={() => { setFiltroDataInicio(''); setFiltroDataFim(''); setTimeout(() => fetchAgenda(), 100) }}
            onExportarExcel={() => handleExportarExcel({ status: 'aprovado', data_inicio: filtroDataInicio, data_fim: filtroDataFim })}
            onExportarPDF={() => handleExportarPDF({ status: 'aprovado', data_inicio: filtroDataInicio, data_fim: filtroDataFim })}
            onPago={handleOpenPagarFromAgenda}
          />
        </TabsContent>

        <TabsContent value="dashboard">
          <DashboardFonteTab dashboardFonte={dashboardFonte} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
